/**
 * Horizontally-scrolling marquee of creator portrait cards.
 *
 * Each card is a fixed-size rectangle with the creator's photo as
 * background. A dark gradient at the bottom of each card hosts the
 * creator's name and role in white/off-white text, matching the
 * graphy.com creator showcase.
 *
 * The row auto-scrolls leftward continuously. The cards are duplicated
 * in-track so the translateX loop reads as a seamless infinite scroll.
 * Hovering pauses the animation so visitors can read a specific card.
 */

type CreatorAvatar = {
  src: string;
  alt: string;
  name: string;
  role: string;
};

import Head from "next/head";
import { getCDNURL } from "@/shared/utils/commons";

const defaultAvatars: CreatorAvatar[] = [
  {
    src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Ankit Awasthi (UPSC Educator).png`,
    alt: "Ankit Awasthi",
    name: "Ankit Awasthi",
    role: "Finance Educator",
  },
  {
    src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Ankur Warikoo (Personal & Financial Coach).png`,
    alt: "Ankur Warikoo",
    name: "Ankur Warikoo",
    role: "Personal & Financial Coach",
  },
  {
    src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Atish Mathur (UPSC Educator).png`,
    alt: "Atish Mathur",
    name: "Atish Mathur",
    role: "UPSC Educator",
  },
  {
    src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Damini Tripathi (Marketing Strategist).png`,
    alt: "Damini Tripathi",
    name: "Damini Tripathi",
    role: "Marketing Strategist",
  },
  {
    src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Priya Mohit Gadhiya (Skill Educator).png`,
    alt: "Priya Mohit Gadhiya",
    name: "Priya Mohit Gadhiya",
    role: "Skill Educator",
  },
  {
    src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Ravi Prakash (MBA Educator).png`,
    alt: "Ravi Prakash",
    name: "Ravi Prakash",
    role: "MBA Educator",
  },
  {
    src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Sagar Sinha (Financial Educator).png`,
    alt: "Sagar Sinha",
    name: "Sagar Sinha",
    role: "Financial Educator",
  },
  {
    src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Sr. Subodh Chaturvedi (Medical Educator).png`,
    alt: "Sr. Subodh Chaturvedi",
    name: "Sr. Subodh Chaturvedi",
    role: "Medical Educator",
  },
];

type Props = {
  avatars?: CreatorAvatar[];
};

// Cards shrink on mobile — the desktop 300×450 is too large on a phone
// where the viewport is ~375px wide. Responsive sizes are driven by a
// CSS custom property so the @keyframes translate math can reference the
// same values. GAP also scales down so the marquee stays proportional.
const CARD_WIDTH_DESKTOP = 300;
const CARD_HEIGHT_DESKTOP = 450;
const CARD_WIDTH_MOBILE = 180;
const CARD_HEIGHT_MOBILE = 270;
const GAP_DESKTOP = 20;
const GAP_MOBILE = 12;

const CreatorArc = ({ avatars = defaultAvatars }: Props) => {
  const items = avatars.length ? avatars : defaultAvatars;
  // Duplicate the track so the translateX loop looks seamless.
  const loop = [...items, ...items];
  // Leftmost card is Lighthouse's LCP candidate. Manual <link rel="preload">
  // makes the request fire while the HTML is still parsing, instead of
  // waiting for styled-jsx to mount the carousel — without that hint
  // LCP load delay sits at ~6.5 s on throttled mobile.
  const lcpImage = items[0];

  return (
    // `overflow-x: clip; overflow-y: visible` via style so the marquee
    // still hides the sideways track seam, but hovered cards can lift
    // up (-6px + scale) without their shadow being cropped. Using
    // `overflow-hidden` would crop the shadow on both axes.
    <div
      className="creator-arc-root relative w-screen left-1/2 right-1/2 -mx-[50vw]"
      style={{ overflowX: "clip", overflowY: "visible", paddingBlock: 20 }}
      aria-label="Featured creators"
    >
      {lcpImage ? (
        <Head>
          <link
            rel="preload"
            as="image"
            href={lcpImage.src}
            // Lowercase per React 18.3 prop-vs-attribute rules.
            {...({ fetchpriority: "high" } as any)}
          />
        </Head>
      ) : null}
      <div
        className="creator-marquee flex"
        style={{ width: "max-content" }}
      >
        {loop.map((a, i) => {
          // The marquee scrolls leftward, so the first few cards in the
          // first half of `loop` are the ones the user sees during initial
          // paint — and Lighthouse picks one of them as the LCP element.
          // First four cards eager-load; the first two also get
          // fetchpriority=high. The rest stay lazy.
          const isAboveTheFold = i < 4;
          const isLcpCandidate = i < 2;
          return (
            <div
              key={i}
              className="creator-arc-card relative shrink-0 overflow-hidden rounded-2xl bg-slateLight shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={a.src}
                alt={a.alt}
                width={CARD_WIDTH_DESKTOP}
                height={CARD_HEIGHT_DESKTOP}
                className="absolute inset-0 h-full w-full object-cover"
                loading={isAboveTheFold ? "eager" : "lazy"}
                decoding="async"
                // Spread as lowercase `fetchpriority` because React 18.3
                // does not recognise the camelCase form on plain DOM
                // elements. Cast to any so TS doesn't complain about the
                // unknown attribute.
                {...({
                  fetchpriority: isLcpCandidate ? "high" : undefined,
                } as any)}
              />
            {/* Dark gradient + text overlay at the bottom. Skipped when the
                card has no name — the US home uses anonymous Creator 01–08
                assets where a text overlay would look out of place. */}
            {a.name ? (
              <div
                className="absolute inset-x-0 bottom-0 px-4 pb-4 pt-12 text-white md:px-5 md:pb-5 md:pt-16"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 70%, rgba(0,0,0,0.85) 100%)",
                }}
              >
                <div className="text-[14px] font-semibold leading-tight md:text-[18px]">
                  {a.name}
                </div>
                {a.role ? (
                  <div className="mt-1 text-[12px] font-normal leading-snug text-white/80 md:text-[14px]">
                    {a.role}
                  </div>
                ) : null}
              </div>
            ) : null}
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .creator-arc-root {
          --card-w: ${CARD_WIDTH_MOBILE}px;
          --card-h: ${CARD_HEIGHT_MOBILE}px;
          --gap: ${GAP_MOBILE}px;
        }
        @media (min-width: 768px) {
          .creator-arc-root {
            --card-w: ${CARD_WIDTH_DESKTOP}px;
            --card-h: ${CARD_HEIGHT_DESKTOP}px;
            --gap: ${GAP_DESKTOP}px;
          }
        }
        .creator-arc-card {
          width: var(--card-w);
          height: var(--card-h);
        }
        .creator-marquee {
          gap: var(--gap);
          animation: creator-scroll 40s linear infinite;
        }
        .creator-marquee:hover {
          animation-play-state: paused;
        }
        @keyframes creator-scroll {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(
              calc(-${items.length} * (var(--card-w) + var(--gap)))
            );
          }
        }
      `}</style>
    </div>
  );
};

export default CreatorArc;
